import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  getProject, updateProject, addCollaborator, removeCollaborator,
  getBalanceSheets, getBusinessPlans, getProjectFinancings, getValuations,
  exportPdf, exportExcel, exportWord
} from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowLeft, Building, Calendar, Users, FileSpreadsheet, 
  TrendingUp, Landmark, Calculator, Download, Plus, Trash2,
  FileText, FileDown, User
} from 'lucide-react';

const ProjectDetail = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [balanceSheets, setBalanceSheets] = useState([]);
  const [businessPlans, setBusinessPlans] = useState([]);
  const [financings, setFinancings] = useState([]);
  const [valuations, setValuations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collaboratorEmail, setCollaboratorEmail] = useState('');
  const [collaboratorDialogOpen, setCollaboratorDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [projectRes, bsRes, bpRes, pfRes, valRes] = await Promise.all([
        getProject(id),
        getBalanceSheets(id),
        getBusinessPlans(id),
        getProjectFinancings(id),
        getValuations(id)
      ]);
      setProject(projectRes.data);
      setBalanceSheets(bsRes.data);
      setBusinessPlans(bpRes.data);
      setFinancings(pfRes.data);
      setValuations(valRes.data);
    } catch (error) {
      toast.error(t('common.error'));
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCollaborator = async () => {
    try {
      await addCollaborator(id, collaboratorEmail);
      setProject({
        ...project,
        collaborators: [...(project.collaborators || []), collaboratorEmail]
      });
      setCollaboratorEmail('');
      setCollaboratorDialogOpen(false);
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleRemoveCollaborator = async (email) => {
    try {
      await removeCollaborator(id, email);
      setProject({
        ...project,
        collaborators: project.collaborators.filter(c => c !== email)
      });
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const handleExport = async (type) => {
    setExporting(true);
    try {
      let response;
      let filename;
      
      if (type === 'pdf') {
        response = await exportPdf(id);
        filename = `report_${project.company_name}.pdf`;
      } else if (type === 'excel') {
        response = await exportExcel(id);
        filename = `report_${project.company_name}.xlsx`;
      } else {
        response = await exportWord(id);
        filename = `report_${project.company_name}.docx`;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Export completato');
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="project-detail">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/projects')} data-testid="back-btn">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">{project.name}</h1>
            <div className="flex items-center gap-4 mt-2 text-muted-foreground">
              <span className="flex items-center gap-1">
                <Building size={16} />
                {project.company_name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={16} />
                {project.fiscal_year}
              </span>
              <Badge variant="secondary">{project.currency}</Badge>
            </div>
          </div>
        </div>
        
        {/* Export buttons */}
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            data-testid="export-pdf-btn"
          >
            <FileText size={16} className="mr-2 text-red-500" />
            PDF
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleExport('excel')}
            disabled={exporting}
            data-testid="export-excel-btn"
          >
            <FileSpreadsheet size={16} className="mr-2 text-green-500" />
            Excel
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleExport('word')}
            disabled={exporting}
            data-testid="export-word-btn"
          >
            <FileDown size={16} className="mr-2 text-blue-500" />
            Word
          </Button>
        </div>
      </div>

      {/* Collaborators */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Users size={20} />
              {t('projects.collaborators')}
            </CardTitle>
            <Dialog open={collaboratorDialogOpen} onOpenChange={setCollaboratorDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="add-collaborator-btn">
                  <Plus size={16} className="mr-2" />
                  {t('projects.addCollaborator')}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('projects.addCollaborator')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={collaboratorEmail}
                      onChange={(e) => setCollaboratorEmail(e.target.value)}
                      placeholder="collaboratore@email.com"
                      data-testid="collaborator-email-input"
                    />
                  </div>
                  <Button onClick={handleAddCollaborator} className="w-full" data-testid="add-collaborator-submit">
                    {t('common.add')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          {project.collaborators?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {project.collaborators.map((email, index) => (
                <Badge key={email} variant="secondary" className="flex items-center gap-2 py-1.5 px-3">
                  <User size={14} />
                  {email}
                  <button 
                    onClick={() => handleRemoveCollaborator(email)}
                    className="ml-1 hover:text-destructive"
                    data-testid={`remove-collaborator-${index}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm">Nessun collaboratore aggiunto</p>
          )}
        </CardContent>
      </Card>

      {/* Modules Tabs */}
      <Tabs defaultValue="balance" className="space-y-6">
        <TabsList className="bg-muted/50 p-1 rounded-xl">
          <TabsTrigger value="balance" className="rounded-lg" data-testid="tab-balance">
            <FileSpreadsheet size={18} className="mr-2" />
            {t('nav.balanceSheet')}
          </TabsTrigger>
          <TabsTrigger value="business" className="rounded-lg" data-testid="tab-business">
            <TrendingUp size={18} className="mr-2" />
            {t('nav.businessPlan')}
          </TabsTrigger>
          <TabsTrigger value="financing" className="rounded-lg" data-testid="tab-financing">
            <Landmark size={18} className="mr-2" />
            {t('nav.projectFinancing')}
          </TabsTrigger>
          <TabsTrigger value="valuation" className="rounded-lg" data-testid="tab-valuation">
            <Calculator size={18} className="mr-2" />
            {t('nav.valuation')}
          </TabsTrigger>
        </TabsList>

        {/* Balance Sheets Tab */}
        <TabsContent value="balance" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('balance.title')}</h3>
            <Link to={`/balance-sheet?project=${id}`}>
              <Button data-testid="new-balance-btn">
                <Plus size={16} className="mr-2" />
                {t('common.add')}
              </Button>
            </Link>
          </div>
          {balanceSheets.length > 0 ? (
            <div className="grid gap-4">
              {balanceSheets.map((bs, index) => (
                <Card key={bs.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`balance-sheet-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{t('balance.year')} {bs.year}</p>
                        <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                          <span>EBITDA: €{bs.indices?.ebitda?.toLocaleString() || 0}</span>
                          <span>ROE: {bs.indices?.roe || 0}%</span>
                          <span>ROI: {bs.indices?.roi || 0}%</span>
                        </div>
                      </div>
                      <Link to={`/balance-sheet/${bs.id}?project=${id}`}>
                        <Button variant="outline" size="sm">{t('common.view')}</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground">
                Nessuna analisi di bilancio. Clicca su "Aggiungi" per iniziare.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Business Plans Tab */}
        <TabsContent value="business" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('bp.title')}</h3>
            <Link to={`/business-plan?project=${id}`}>
              <Button data-testid="new-bp-btn">
                <Plus size={16} className="mr-2" />
                {t('common.add')}
              </Button>
            </Link>
          </div>
          {businessPlans.length > 0 ? (
            <div className="grid gap-4">
              {businessPlans.map((bp, index) => (
                <Card key={bp.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`business-plan-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{bp.name}</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {bp.start_year} - {bp.start_year + bp.years - 1} ({bp.years} anni)
                        </p>
                      </div>
                      <Link to={`/business-plan/${bp.id}?project=${id}`}>
                        <Button variant="outline" size="sm">{t('common.view')}</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground">
                Nessun business plan. Clicca su "Aggiungi" per iniziare.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Project Financing Tab */}
        <TabsContent value="financing" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('pf.title')}</h3>
            <Link to={`/project-financing?project=${id}`}>
              <Button data-testid="new-pf-btn">
                <Plus size={16} className="mr-2" />
                {t('common.add')}
              </Button>
            </Link>
          </div>
          {financings.length > 0 ? (
            <div className="grid gap-4">
              {financings.map((pf, index) => (
                <Card key={pf.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`financing-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{pf.name}</p>
                        <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                          <span>VAN: €{pf.npv?.toLocaleString()}</span>
                          <span>TIR: {pf.irr ? `${pf.irr}%` : 'N/A'}</span>
                          <span>Payback: {pf.payback_period ? `${pf.payback_period} anni` : 'N/A'}</span>
                        </div>
                      </div>
                      <Link to={`/project-financing/${pf.id}?project=${id}`}>
                        <Button variant="outline" size="sm">{t('common.view')}</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground">
                Nessuna analisi di project financing. Clicca su "Aggiungi" per iniziare.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Valuations Tab */}
        <TabsContent value="valuation" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">{t('val.title')}</h3>
            <Link to={`/valuation?project=${id}`}>
              <Button data-testid="new-val-btn">
                <Plus size={16} className="mr-2" />
                {t('common.add')}
              </Button>
            </Link>
          </div>
          {valuations.length > 0 ? (
            <div className="grid gap-4">
              {valuations.map((val, index) => (
                <Card key={val.id} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`valuation-${index}`}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-semibold">{val.name}</p>
                        <div className="flex gap-4 mt-2 text-sm text-muted-foreground">
                          <Badge variant="outline">{val.method.toUpperCase()}</Badge>
                          {val.average_value && <span>Valore: €{val.average_value?.toLocaleString()}</span>}
                        </div>
                      </div>
                      <Link to={`/valuation/${val.id}?project=${id}`}>
                        <Button variant="outline" size="sm">{t('common.view')}</Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-8 text-center text-muted-foreground">
                Nessuna valutazione. Clicca su "Aggiungi" per iniziare.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProjectDetail;
